import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Clock, Mail, Phone, MessageSquare, Send } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");
const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_META = {
  in_review: { label: "In valutazione", color: "bg-amber-100 text-amber-800", icon: Clock },
  quotata: { label: "Quotata", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  rifiutata: { label: "Rifiutata", color: "bg-red-100 text-red-800", icon: XCircle },
  confermata: { label: "Confermata → Prenotazione", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
};

export default function CasaMadreRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("in_review");
  const [quoting, setQuoting] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/config-requests");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (filter === "all" ? rows : rows.filter((r) => r.status === filter)), [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, in_review: 0, quotata: 0, rifiutata: 0, confermata: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div>
          <h1 className="font-heading font-bold text-3xl mb-1">Richieste configurazione</h1>
          <p className="text-sm text-muted-foreground">Configurazioni personalizzate richieste dai clienti B2C sulla vetrina.</p>
        </div>

        <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
          {[
            { k: "in_review", label: "In valutazione" },
            { k: "quotata", label: "Quotate" },
            { k: "confermata", label: "Confermate" },
            { k: "rifiutata", label: "Rifiutate" },
            { k: "all", label: "Tutte" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-3 h-8 text-xs rounded-sm inline-flex items-center gap-1.5 ${filter === f.k ? "bg-foreground text-white" : "border border-border hover:bg-accent"}`}>
              {f.label} <span className="opacity-70">({counts[f.k] || 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted-foreground" data-testid="empty-requests">
            Nessuna richiesta {filter !== "all" ? `nello stato "${STATUS_META[filter]?.label || filter}"` : ""}.
          </div>
        ) : (
          <div className="space-y-2" data-testid="requests-list">
            {filtered.map((r) => (
              <RequestRow key={r.id} req={r}
                onQuote={() => setQuoting(r)}
                onReject={() => setRejecting(r)} />
            ))}
          </div>
        )}
      </div>

      {quoting && <QuoteDialog req={quoting} onClose={(ok) => { setQuoting(null); if (ok) load(); }} />}
      {rejecting && <RejectDialog req={rejecting} onClose={(ok) => { setRejecting(null); if (ok) load(); }} />}
    </AdminLayout>
  );
}

function RequestRow({ req, onQuote, onReject }) {
  const s = STATUS_META[req.status] || STATUS_META.in_review;
  const Icon = s.icon;
  return (
    <div className="border border-border rounded-sm p-4 grid lg:grid-cols-12 gap-4" data-testid={`request-${req.id}`}>
      <div className="lg:col-span-4">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${s.color}`}>
            <Icon className="w-3 h-3" /> {s.label}
          </span>
          <span className="text-[10px] text-muted-foreground">{fmtDate(req.created_at)}</span>
        </div>
        <div className="font-heading font-bold">{req.product_name}</div>
        <div className="text-[11px] font-mono text-muted-foreground">{req.sku_madre}</div>
      </div>

      <div className="lg:col-span-3 text-xs space-y-0.5">
        <div className="font-medium">{req.customer_name}</div>
        <div className="text-muted-foreground inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {req.customer_email}</div>
        {req.customer_phone && <div className="text-muted-foreground inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {req.customer_phone}</div>}
      </div>

      <div className="lg:col-span-3 text-xs">
        <div className="overline text-muted-foreground mb-1">Configurazione</div>
        <ul className="space-y-0.5">
          {Object.entries(req.combination || {}).map(([k, v]) => (
            <li key={k}><span className="text-muted-foreground">{k}:</span> <strong>{v.label || v.code || String(v)}</strong></li>
          ))}
        </ul>
        {req.customer_notes && (
          <div className="mt-2 pt-2 border-t border-border/60 text-muted-foreground italic flex gap-1">
            <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" /> {req.customer_notes}
          </div>
        )}
      </div>

      <div className="lg:col-span-2 flex flex-col gap-1.5">
        <div className="text-xs">
          <div className="text-muted-foreground">Indicativo</div>
          <div className="font-mono font-medium">{fmtEuro(req.prezzo_indicativo)}</div>
          {req.prezzo_quotato && (
            <>
              <div className="text-muted-foreground mt-1">Quotato</div>
              <div className="font-mono font-bold text-emerald-700">{fmtEuro(req.prezzo_quotato)}</div>
              <div className="text-[10px] text-muted-foreground">{req.lead_time_gg} gg</div>
            </>
          )}
        </div>
        {req.status === "in_review" && (
          <div className="flex flex-col gap-1 mt-1">
            <button onClick={onQuote} data-testid={`quote-${req.id}`}
              className="h-8 text-[11px] font-medium bg-emerald-600 text-white rounded-sm hover:bg-emerald-700">
              Quota
            </button>
            <button onClick={onReject} data-testid={`reject-${req.id}`}
              className="h-8 text-[11px] font-medium border border-red-300 text-red-700 rounded-sm hover:bg-red-50">
              Rifiuta
            </button>
          </div>
        )}
        {req.status === "rifiutata" && req.reject_reason && (
          <div className="text-[10px] text-red-700 italic mt-1">Motivo: {req.reject_reason}</div>
        )}
      </div>
    </div>
  );
}

function QuoteDialog({ req, onClose }) {
  const [prezzo, setPrezzo] = useState(req.prezzo_indicativo || 0);
  const [lead, setLead] = useState(req.lead_time_indicativo_gg || 45);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/config-requests/${req.id}/quote`, { prezzo_quotato: Number(prezzo), lead_time_gg: Number(lead), note_casa_madre: note });
      toast.success("Quotazione inviata al cliente");
      onClose(true);
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="quote-dialog">
        <DialogHeader><DialogTitle>Quota richiesta — {req.product_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">Cliente: {req.customer_name} · {req.customer_email}</div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Prezzo definitivo €</label>
            <input type="number" step="0.10" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} data-testid="quote-price"
              className="w-full h-10 border border-border rounded-sm px-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead time (giorni)</label>
            <input type="number" min="0" value={lead} onChange={(e) => setLead(e.target.value)} data-testid="quote-lead"
              className="w-full h-10 border border-border rounded-sm px-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note (facoltative)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="quote-note"
              rows={3} className="w-full border border-border rounded-sm px-2 py-1.5 text-sm resize-y" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => onClose(false)} className="flex-1 h-10 border border-border rounded-sm text-sm">Annulla</button>
            <button onClick={submit} disabled={saving} data-testid="quote-submit"
              className="flex-1 h-10 bg-emerald-600 text-white rounded-sm text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Invia quotazione
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ req, onClose }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Inserisci un motivo"); return; }
    setSaving(true);
    try {
      await api.put(`/config-requests/${req.id}/reject`, { reject_reason: reason });
      toast.success("Richiesta rifiutata, cliente notificato");
      onClose(true);
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Rifiuta richiesta — {req.product_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motivo (visibile al cliente)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason"
              rows={4} placeholder="Es. materiale non più disponibile, combinazione non fattibile..."
              className="w-full border border-border rounded-sm px-2 py-1.5 text-sm resize-y" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => onClose(false)} className="flex-1 h-10 border border-border rounded-sm text-sm">Annulla</button>
            <button onClick={submit} disabled={saving} data-testid="reject-submit"
              className="flex-1 h-10 bg-red-600 text-white rounded-sm text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Conferma rifiuto
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
